// Who is logged in. The app opens at once with the last known user (spec V1 AC1: within a second, also offline)
// and checks the session in the background. Only a real 401 means: log in again.

import { api, ApiError, isNetworkError } from "./api";
import { clearLocalData, db, getMeta, setMeta } from "./db";
import { sync } from "./sync";

export interface Me {
  user: { id: string; email: string; displayName: string; role: "owner" | "member" };
  settings: {
    day_starts_at: string;
    personal_space_id: string | null;
    shared_space_id: string | null;
    morning_mode?: boolean;
    ai_enabled?: boolean;
    show_partner?: boolean;
    wind_down?: boolean;
  } | null;
  /** PK and Tsjits (the people of the shared space with an account). */
  household?: { id: string; name: string; role: "owner" | "member" }[];
}

export type SessionState = { kind: "loading" } | { kind: "anonymous" } | { kind: "user"; me: Me };

const CHECK_TIMEOUT_MS = 8000;

export async function cachedSession(): Promise<SessionState> {
  const me = await getMeta<Me>("me");
  return me ? { kind: "user", me } : { kind: "anonymous" };
}

/**
 * Asks the server who is logged in. Returns null when that cannot be known right now
 * (offline, server restarting, timeout): the caller then keeps what it has.
 */
export async function verifySession(): Promise<SessionState | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);
  try {
    const me = await api<Me>("/me", { signal: controller.signal });
    const previous = await getMeta<Me>("me");
    // Another account on this device: never show the previous user's data.
    if (previous && previous.user.id !== me.user.id) await clearLocalData();
    await setMeta("me", me);
    // The server's setting, unless a change made on this device is still on its way.
    const pending = (await db.outbox.toArray()).some((o) => o.name === "settings.setMorningMode");
    if (!pending && me.settings) await setMeta("morningMode", me.settings.morning_mode !== false);
    const aiPending = (await db.outbox.toArray()).some((o) => o.name === "settings.setAi");
    if (!aiPending && me.settings) await setMeta("aiEnabled", me.settings.ai_enabled !== false);
    const partnerPending = (await db.outbox.toArray()).some((o) => o.name === "settings.setShowPartner");
    if (!partnerPending && me.settings) await setMeta("showPartner", me.settings.show_partner !== false);
    const windDownPending = (await db.outbox.toArray()).some((o) => o.name === "settings.setWindDown");
    if (!windDownPending && me.settings) await setMeta("windDown", me.settings.wind_down !== false);
    return { kind: "user", me };
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return { kind: "anonymous" };
    if (isNetworkError(error)) return null;
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

export async function logout(): Promise<void> {
  // First the sync: a pull still on its way would otherwise write PK's data back after the clear.
  await sync.halt();
  await api("/auth/logout", { body: {} }).catch(() => {});
  await clearLocalData();
}
